import { get } from 'lodash';
import moment from 'moment-timezone';
import {
    ErrorType,
    ErrorTypes
} from '../models/Errors';
import ConfigurationValuesService, { IConfigurationValuesService } from './ConfigurationValuesService';
import HttpService, { IHttpService } from './HttpService';

export interface IAuthenticateParams {
    candidateId: string;
    documentId: string;
    translatorEmailAddress: string;
}

export interface ITranslatorData {
    address: {
        administrativeLevel1?: string;
        administrativeLevel1Short?: string;
        administrativeLevel2?: string;
        administrativeLevel2Short?: string;
        country?: string;
        countryShort?: string;
        formattedAddress?: string;
        geohash?: string;
        latitude?: number;
        locality?: string;
        longitude?: number;
        postalCode?: string;
        postalCodeSuffix?: string;
        route?: string;
        streetNumber?: string;
    };
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
}

export interface IAuthenticatedDetails {
    firstName: string;
    i9Part1DocumentId?: string;
    i9Part1SignedOn: string;
    lastName: string;
    middleName?: string;
    onboardingStatus?: string;
    otherLastNamesUsed?: string;
    personId?: string;
    phoneNumber?: string;
    translatorData: ITranslatorData;
    errorCode?: ErrorType;
}

export interface IConfirmParams {
    candidateId: string;
    documentId: string;
    translatorData: ITranslatorData;
}

export interface IDeclineParams {
    candidateId: string;
    documentId: string;
}

export interface IAuthorisationService {
    authenticateAsync(params: IAuthenticateParams): Promise<IAuthenticatedDetails>;
    confirmAsync({ candidateId, documentId, translatorData }: IConfirmParams): Promise<any>;
    declineAsync({ candidateId, documentId }: IDeclineParams): Promise<any>;
}

export default class AuthorisationService implements IAuthorisationService {
    private readonly httpService: IHttpService;
    private readonly configurationValuesService: IConfigurationValuesService;
    private readonly urlPrefix: string;

    constructor() {
        this.httpService = new HttpService();
        this.configurationValuesService = ConfigurationValuesService.getInstance();
        this.urlPrefix = `${this.configurationValuesService.apiUrl}/worker-onboarding/external/workeronboarding/translator/i9/`;
    }

    async authenticateAsync({ candidateId, documentId, translatorEmailAddress }: IAuthenticateParams): Promise<IAuthenticatedDetails> {
        try {
            const url = `${this.urlPrefix}/${candidateId}`;
            const response = await this.httpService.post(url, { documentId, translatorEmailAddress });
            return response.data;
        } catch (e) {
            return  get(e, 'response.data') || { errorCode: ErrorTypes.ErrorOther } as IAuthenticatedDetails;
         }
    }

    async confirmAsync({ candidateId, documentId, translatorData }: IConfirmParams): Promise<any> {
        const clientTimezone = moment.tz.guess();

        try {
            const url = `${this.urlPrefix}${candidateId}/approve`;
            const body = {
                clientTimezone,
                documentId,
                translatorData
            };
            const response = await this.httpService.post(url, body);
            return response.data;
        } catch (e) {
            return  get(e, 'response.data') || { errorCode: ErrorTypes.ErrorOther };
        }
    }

    async declineAsync({ candidateId, documentId }: IDeclineParams): Promise<any> {
        try {
            const url = `${this.urlPrefix}${candidateId}/decline`;
            const response = await this.httpService.post(url, { documentId });
            return response.data;
        } catch (e) {
            return  get(e, 'response.data') || { errorCode: ErrorTypes.ErrorOther };
        }
    }
}



// WEBPACK FOOTER //
// ./src/services/AuthorisationService.ts